import { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth, ROLE_LABELS } from "../context/AuthContext";
import { Trash2, UserPlus, Pencil } from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { email: "", password: "", name: "", role: "employee", location_id: "", franchise_name: "" };
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");

  const refresh = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    refresh();
    api.get("/locations").then((r) => setLocations(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/users", { ...form, location_id: form.location_id || null, franchise_name: form.franchise_name || null });
      setOpen(false);
      setForm(empty);
      refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const body = {
        email: form.email, name: form.name, role: form.role,
        location_id: form.location_id || null,
        franchise_name: form.franchise_name || null,
      };
      if (form.password) body.password = form.password;
      await api.patch(`/users/${editing.id}`, body);
      setEditing(null);
      setForm(empty);
      refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const startEdit = (u) => {
    setForm({
      email: u.email, password: "", name: u.name, role: u.role,
      location_id: u.location_id || "", franchise_name: u.franchise_name || "",
    });
    setErr("");
    setEditing(u);
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    await api.delete(`/users/${u.id}`);
    refresh();
  };

  const locMap = Object.fromEntries(locations.map((l) => [l.id, l.name]));

  return (
    <Layout>
      <PageHeader
        eyebrow="Administration"
        title="Users & Permissions"
        description="Manage who has access — and at what level — across the network."
        actions={isOwner && (
          <button
            onClick={() => setOpen(true)}
            data-testid="add-user-btn"
            className="h-11 px-5 rounded-xl bg-foreground text-background font-medium text-sm inline-flex items-center gap-2 hover:bg-foreground/90"
          >
            <UserPlus className="h-4 w-4" /> Add user
          </button>
        )}
      />

      <div className="luxury-card overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow font-medium">Name</th>
              <th className="px-5 py-3 label-eyebrow font-medium">Email</th>
              <th className="px-5 py-3 label-eyebrow font-medium">Role</th>
              <th className="px-5 py-3 label-eyebrow font-medium">Location</th>
              {isOwner && <th className="px-5 py-3"></th>}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border">
                <td className="px-5 py-3.5 font-medium">{u.name}</td>
                <td className="px-5 py-3.5 text-muted-foreground">{u.email}</td>
                <td className="px-5 py-3.5">
                  <span className="text-[11px] px-2.5 py-1 rounded-full bg-secondary font-medium">
                    {ROLE_LABELS[u.role]}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-muted-foreground">{locMap[u.location_id] || "—"}</td>
                {isOwner && (
                  <td className="px-5 py-3.5 text-right space-x-1 whitespace-nowrap">
                    <button
                      onClick={() => startEdit(u)}
                      data-testid={`edit-user-${u.id}`}
                      className="text-muted-foreground hover:text-foreground p-1 inline-flex items-center gap-1 text-xs"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    {u.id !== user.id && (
                      <button
                        onClick={() => del(u)}
                        data-testid={`delete-user-${u.id}`}
                        className="text-muted-foreground hover:text-destructive p-1 inline-flex items-center gap-1 text-xs"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <UserFormModal
          title="Add Team Member"
          subtitle="Create credentials for a new user."
          form={form}
          setForm={setForm}
          onSubmit={submit}
          onClose={() => { setOpen(false); setErr(""); }}
          submitLabel="Create user"
          err={err}
          locations={locations}
          mode="create"
          testid="add-user-modal"
        />
      )}
      {editing && (
        <UserFormModal
          title={`Edit ${editing.name}`}
          subtitle="Update name, email, role or reset the password."
          form={form}
          setForm={setForm}
          onSubmit={submitEdit}
          onClose={() => { setEditing(null); setErr(""); }}
          submitLabel="Save changes"
          err={err}
          locations={locations}
          mode="edit"
          testid="edit-user-modal"
        />
      )}
    </Layout>
  );
}

function UserFormModal({ title, subtitle, form, setForm, onSubmit, onClose, submitLabel, err, locations, mode, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-2xl border border-border w-full max-w-md p-8 max-h-[92vh] overflow-y-auto" data-testid={testid}>
        <h2 className="font-display text-2xl font-light tracking-tight mb-1">{title}</h2>
        <p className="text-sm text-muted-foreground mb-6">{subtitle}</p>

        <form onSubmit={onSubmit} className="space-y-3">
          <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="user-name" required />
          <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="user-email" required />
          <Input
            label={mode === "edit" ? "Password (leave blank to keep current)" : "Password"}
            type="password"
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
            testid="user-password"
            required={mode === "create"}
          />

          <div>
            <label className="label-eyebrow mb-2 block">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="user-role"
              className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm"
            >
              {Object.entries(ROLE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          {form.role === "franchisee" && (
            <div>
              <label className="label-eyebrow mb-2 block">
                Franchise Name <span className="text-muted-foreground normal-case tracking-normal">(auto-fills &ldquo;Customer&rdquo; on their orders)</span>
              </label>
              <input
                value={form.franchise_name}
                onChange={(e) => setForm({ ...form, franchise_name: e.target.value })}
                placeholder="Woodland · Rice Village · etc."
                data-testid="user-franchise-name"
                className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm"
              />
            </div>
          )}

          <div>
            <label className="label-eyebrow mb-2 block">Location (optional)</label>
            <select
              value={form.location_id}
              onChange={(e) => setForm({ ...form, location_id: e.target.value })}
              data-testid="user-location"
              className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm"
            >
              <option value="">— None —</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>

          {err && <div className="text-xs text-destructive">{err}</div>}

          <div className="flex gap-2 pt-3">
            <button type="button" onClick={onClose} className="h-11 flex-1 rounded-xl border border-border bg-card text-sm font-medium">
              Cancel
            </button>
            <button type="submit" data-testid="submit-user-form" className="h-11 flex-1 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90">
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input({ label, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="label-eyebrow mb-2 block">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
    </div>
  );
}
